import React, { useEffect, useState } from "react";
import { ReactSortable } from "react-sortablejs";
import PropTypes from "prop-types";

import "./TaskList.css";

import { useMappedBooleanState } from "hooks";
import { FeatureFlag } from "components/FeatureFlag";

import { useAccessTypeStore } from "scenes/Hubly/components/Workspace/components/hooks";
import {
  useDragDropContext,
  useDragDropEventManager,
  WithDragDropContext,
} from "scenes/Hubly/components/Workspace/components/WorkflowContainer/components/WorkflowEditor/hooks";

import { TaskConditionsGroupView } from "..";
import Task from "../Task";
import {
  Conditions,
  LegacyTaskCondition,
  TaskDetails,
  TaskEmailTemplate,
  TaskFormIcon,
  TaskFutureReminder,
  TaskPopup,
  TaskTimeEstimate,
} from "../Task/components";
import { DetailsModal } from "../Task/components/TaskDetails/components";
import { hasDetails } from "../Task/components/TaskDetails/helper";

export function TaskList({
  workflowId,
  taskGroups,
  disableDnd,
  onTaskUpdated,
  onTaskDeleted,
  onTaskReOrdered,
  ...otherProps
}) {
  const [detailsModalProps, showDetailsProps] = useState({});
  const [detailsContent, setDetailsContent] = useState({});
  const [openedTasksForm, openTaskForm, closeTaskForm] =
    useMappedBooleanState();

  const { hasAccess } = useAccessTypeStore();
  const {
    itemList: taskList,
    modifyRootList,
    modifySubList,
  } = useDragDropEventManager(taskGroups, onTaskReOrdered);
  const [, dispatch] = useDragDropContext();

  useEffect(() => {
    dispatch({ type: "set-disabled", value: disableDnd });
  }, [disableDnd, dispatch]);

  const handleOnFormClick = (id) => {
    return () => {
      return openedTasksForm[id] ? closeTaskForm(id) : openTaskForm(id);
    };
  };

  if (taskList.length === 0) {
    return (
      <div style={{ display: "block", color: "grey", paddingBottom: "0em" }}>
        No tasks yet
      </div>
    );
  }

  return (
    <ReactSortable
      list={taskList}
      disabled={!hasAccess() || disableDnd}
      setList={modifyRootList}
      group={`taskGroups-${workflowId}`}
      ghostClass="hubly-dnd-ghost"
      animation={100}
      handle=".task-drag-handle"
      onStart={() => {
        dispatch({ type: "set-dragging", value: true });
      }}
      onEnd={() => {
        dispatch({ type: "set-dragging", value: false });
      }}
      forceFallback
    >
      {taskList.map((tg, i) => {
        return (
          <TaskConditionsGroupView
            key={tg?.id}
            taskGroup={tg}
            workflowId={workflowId}
            disabled={!hasAccess()}
            onTaskConfigChange={(value) => {
              onTaskUpdated({ updatedTask: value, type: "editMultiple" });
            }}
            onTaskOrderModified={(newTaskList) => {
              modifySubList(i, tg, newTaskList);
            }}
            onDragged={(isDragging) => {
              dispatch({ type: "set-dragging", value: isDragging });
            }}
          >
            {(ts) => {
              return ts.map((task) => {
                return (
                  <Task
                    key={task.id}
                    task={task}
                    onUpdated={onTaskUpdated}
                    taskDetailsContent={detailsContent[task.id]}
                    showTaskForm={openedTasksForm[task.id]}
                    onFormClick={handleOnFormClick(task.id)}
                    {...otherProps}
                  >
                    <FeatureFlag id="task-form">
                      <TaskFormIcon
                        onClick={handleOnFormClick(task.id)}
                        hasForm={
                          openedTasksForm[task.id] || Boolean(task.form?.id)
                        }
                      />
                    </FeatureFlag>
                    <TaskFutureReminder
                      task={task}
                      index={i}
                      numTasks={taskGroups.length}
                      onTaskConfigChange={onTaskUpdated}
                    />
                    <TaskEmailTemplate
                      task={task}
                      onTaskConfigChange={onTaskUpdated}
                    />
                    <TaskTimeEstimate
                      task={task}
                      onTaskConfigChange={onTaskUpdated}
                    />
                    <TaskDetails
                      task={task}
                      expanded={
                        detailsContent[task.id] !== undefined &&
                        detailsContent[task.id] !== null
                      }
                      onTaskConfigChange={(updatedTask) => {
                        setDetailsContent({});
                        showDetailsProps((prevState) => {
                          return {
                            ...prevState,
                            [task.id]: null,
                          };
                        });
                        onTaskUpdated(updatedTask);
                      }}
                      onShowTaskDetailContents={(details) => {
                        setDetailsContent((prevState) => {
                          return {
                            ...prevState,
                            [task.id]: details,
                          };
                        });
                      }}
                      onShowDetailProps={(props) => {
                        showDetailsProps((prevState) => {
                          return {
                            ...prevState,
                            [task.id]: props,
                          };
                        });
                      }}
                    >
                      {(props) => {
                        return (
                          detailsModalProps[task.id] && (
                            <DetailsModal
                              {...detailsModalProps[task.id]}
                              {...props}
                            />
                          )
                        );
                      }}
                    </TaskDetails>

                    <FeatureFlag id="conditional-logic">
                      <Conditions
                        workflowId={workflowId}
                        item={task}
                        onConfigChange={onTaskUpdated}
                      />
                    </FeatureFlag>
                    <FeatureFlag id="conditional-logic" showOnDisabled>
                      <LegacyTaskCondition
                        task={task}
                        onTaskConfigChange={onTaskUpdated}
                      />
                    </FeatureFlag>

                    {hasAccess() && (
                      <TaskPopup
                        id={task.id}
                        title={task.title}
                        workflowId={workflowId}
                        onTaskDeleted={onTaskDeleted}
                        onEditTask={() => {
                          showDetailsProps((prevState) => {
                            return {
                              ...prevState,
                              [task.id]: {
                                task: task,
                                isCreating: !hasDetails(task),
                              },
                            };
                          });
                        }}
                        onCommentTask={() => {
                          // TODO HUB-2441: onCommentTask Callback when integrating with client card
                        }}
                      />
                    )}
                  </Task>
                );
              });
            }}
          </TaskConditionsGroupView>
        );
      })}
    </ReactSortable>
  );
}

TaskList.propTypes = {
  taskGroups: PropTypes.array,
  workflowId: PropTypes.string,
  disableDnd: PropTypes.bool,
  onTaskUpdated: PropTypes.func.isRequired,
  onTaskReOrdered: PropTypes.func.isRequired,
  onTaskDeleted: PropTypes.func.isRequired,
};

TaskList.defaultProps = {
  disableDnd: false,
  workflowId: "",
  taskGroups: [],
};

export default WithDragDropContext(TaskList);
